import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatError, useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, ExternalLink, Trash2 } from "lucide-react";
import { PROVIDERS } from "@/lib/booking";
import PendingBookingBanner from "@/components/PendingBookingBanner";
import ProviderButtons from "@/components/ProviderButtons";
import { formatTime, formatTimeRange } from "@/lib/time";
import DirectionsPanel from "@/components/DirectionsPanel";

export default function DinnerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [dn, setDn] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [members, setMembers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotDiff, setSnapshotDiff] = useState(null);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const [reserveForm, setReserveForm] = useState({ confirmation_number:"", provider_link:"" });
  const [ratingForm, setRatingForm] = useState({ member_id:"", stars:5, comment:"" });
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [households, setHouseholds] = useState([]);
  const [staticMap, setStaticMap] = useState(null);

  const load = useCallback(() => Promise.all([
    api.get(`/dinners/${id}`).then(r=>r.data),
    api.get(`/dinners/${id}/ratings`).then(r=>r.data),
    api.get("/restaurants").then(r=>r.data),
    api.get("/members").then(r=>r.data),
    api.get(`/dinners/${id}/rsvps`).then(r=>r.data).catch(()=>[]),
    api.get(`/dinners/${id}/preference-snapshot`).then(r=>r.data).catch(()=>({members:[]})),
    api.get(`/dinners/${id}/preference-snapshot/diff`).then(r=>r.data).catch(()=>({changes:[]})),
    api.get("/households").then(r=>r.data).catch(()=>[]),
    api.get(`/dinners/${id}/static-map`).then(r=>r.data).catch(()=>null),
  ]).then(([d,rt,r,m,rs,sn,sd,hh,sm])=>{setDn(d);setRatings(rt);setRestaurants(r);setMembers(m);setRsvps(rs);setSnapshot(sn);setSnapshotDiff(sd);setHouseholds(hh);setStaticMap(sm);}), [id]);
  useEffect(()=>{ load(); },[load]);

  if (!dn) return <div className="text-muted-foreground">Loading…</div>;
  const rest = restaurants.find(r=>r.id===dn.restaurant_id);
  const memberName = (mid) => members.find(m=>m.id===mid);
  const setStatus = async (newAction) => {
    try {
      if (newAction === "confirm") {
        await api.post(`/dinners/${id}/reservation`, {
          status: "confirmed", provider: "manual",
          confirmation_number: reserveForm.confirmation_number,
          provider_link: reserveForm.provider_link,
        });
        toast.success("Reservation confirmed. Guests will be notified.");
      } else if (newAction === "complete") {
        await api.post(`/dinners/${id}/complete`);
        toast.success("Dinner marked awaiting ratings");
      }
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const sendRecap = async () => {
    try { await api.post(`/dinners/${id}/send-recap`); toast.success("Recap queued to organizer + guests"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const suggestNewDate = async () => {
    setLoadingSuggest(true);
    try {
      const { data } = await api.post(`/dinners/${id}/auto-reschedule`, { days_lookahead: 21 });
      setSuggestion(data);
      const fully = (data.suggestions || []).filter(s => s.all_available).length;
      if (fully > 0) toast.success(`${fully} fully-available date${fully===1?"":"s"} found`);
      else toast.warning(`No fully-available dates in the next 21 days — showing best partials`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoadingSuggest(false); }
  };
  const submitRating = async () => {
    if (!ratingForm.member_id) return toast.error("Select a member");
    try {
      await api.post(`/dinners/${id}/ratings`, ratingForm);
      toast.success("Rating saved");
      setRatingForm({ member_id:"", stars:5, comment:"" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const deleteDinner = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/dinners/${id}`);
      const c = data.cascaded || {};
      const cascCount = Object.values(c).reduce((a,b)=>a+b, 0);
      toast.success(`Dinner deleted${cascCount ? ` · removed ${cascCount} related record(s)` : ""}`);
      nav("/history");
    } catch (e) { toast.error(formatError(e)); setDeleting(false); }
  };

  return (
    <div className="space-y-6">
      <PendingBookingBanner/>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline mb-1">{dn.status.replaceAll("_"," ")}</div>
          <h1 className="font-serif text-3xl">{rest?.name || "Unassigned"}</h1>
          <div className="text-sm mono text-muted-foreground mt-1">{dn.dinner_date} · {formatTime(dn.approved_time || dn.recommended_time)}</div>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={()=>setPendingDelete(true)} data-testid="dinner-delete-btn"
                  className="text-destructive hover:bg-destructive/10 border-destructive/40">
            <Trash2 className="w-4 h-4 mr-1"/>Delete dinner
          </Button>
        )}
      </div>

      <Dialog open={pendingDelete} onOpenChange={setPendingDelete}>
        <DialogContent data-testid="delete-dinner-dialog">
          <DialogHeader>
            <DialogTitle>Delete this dinner permanently?</DialogTitle>
            <DialogDescription>
              Removes the dinner and every rating, RSVP, snapshot, and reservation linked to it. Use this to clean up test or bogus data that skews analytics. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPendingDelete(false)} disabled={deleting} data-testid="cancel-delete-btn">Cancel</Button>
            <Button onClick={deleteDinner} disabled={deleting} data-testid="confirm-delete-btn"
                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
              {deleting ? "Deleting…" : "Delete dinner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="overline mb-1">Participants</div>
            <div className="text-sm space-y-1">
              {dn.participant_member_ids.map(mid=>{ const m=memberName(mid); return <div key={mid}>{m?.first_name} {m?.last_name}</div>; })}
            </div>
          </div>
          <div>
            <div className="overline mb-1">Common window</div>
            <div className="text-sm mono">{formatTimeRange(dn.common_window?.earliest, dn.common_window?.latest)}</div>
            <div className="overline mt-3 mb-1">Recommended time</div>
            <div className="text-sm mono">{formatTime(dn.recommended_time)}</div>
          </div>
        </div>
      </Card>

      {rest && households.length > 0 && (() => {
        // Only show households that had at least one participant in this dinner
        const participantHouseholdIds = new Set(
          dn.participant_member_ids
            .map(mid => members.find(m => m.id === mid)?.household_id)
            .filter(Boolean)
        );
        const relevant = households
          .filter(h => participantHouseholdIds.has(h.id))
          .map(h => ({ ...h, _has_participant: true }));
        if (relevant.length === 0) return null;
        return (
          <Card className="p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="overline mb-1">Directions</div>
                <h3 className="font-serif text-xl">Get to {rest.name}</h3>
                <div className="text-xs text-muted-foreground mt-0.5">{rest.address}{rest.address ? ", " : ""}{rest.city}, {rest.state}</div>
              </div>
              {staticMap?.url && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${rest.latitude},${rest.longitude}`}
                   target="_blank" rel="noreferrer"
                   data-testid="dinner-static-map-link"
                   className="relative rounded-lg overflow-hidden border hover:ring-2 hover:ring-primary transition-shadow shrink-0"
                   title="Open the group's map in Google Maps">
                  <img src={staticMap.url} alt="Overview map of households and restaurant"
                       width={300} height={200}
                       data-testid="dinner-static-map-img"
                       className="block w-[300px] h-[200px] object-cover"
                       loading="lazy"/>
                  <div className="absolute bottom-1 right-1 bg-background/80 backdrop-blur px-1.5 py-0.5 rounded text-[9px] mono uppercase tracking-wider pointer-events-none">
                    R = {rest.name.slice(0, 14)}{rest.name.length > 14 ? "…" : ""}
                  </div>
                </a>
              )}
            </div>
            <DirectionsPanel households={relevant} restaurant={rest} memberIds={dn.participant_member_ids}/>
          </Card>
        );
      })()}

      {(dn.status === "approved" || dn.status === "external_confirmation_required" || dn.status === "reservation_pending") && (
        <Card className="p-6">
          <h3 className="font-serif text-xl mb-3">Complete reservation</h3>
          <p className="text-xs text-muted-foreground mb-3">The Common Dinner facilitates reservations. Call the restaurant or book on their site, then record the confirmation here.</p>
          {rest && (
            <div className="mb-4" data-testid="booking-quick-links">
              <ProviderButtons
                ctx={{ name: rest.name, city: rest.city, date: dn.dinner_date,
                       time: dn.approved_time || dn.recommended_time,
                       party: dn.participant_member_ids.length }}
                preferredId={rest.preferred_provider}
                onOpen={(p, url) => {
                  api.post(`/dinners/${id}/booking-intent`, { provider: p.id, provider_url: url }).catch(()=>{});
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              />
              {rest.website && (
                <Button variant="ghost" size="sm" asChild data-testid="open-website-btn" className="mt-2">
                  <a href={rest.website} target="_blank" rel="noreferrer" className="flex items-center gap-1">
                    <ExternalLink className="w-3 h-3"/>Restaurant site
                  </a>
                </Button>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Confirmation number</Label><Input data-testid="confirmation-number-input" value={reserveForm.confirmation_number} onChange={e=>setReserveForm({...reserveForm,confirmation_number:e.target.value})}/></div>
            <div><Label>Provider link (optional)</Label><Input value={reserveForm.provider_link} onChange={e=>setReserveForm({...reserveForm,provider_link:e.target.value})}/></div>
          </div>
          <Button className="mt-4" onClick={()=>setStatus("confirm")} data-testid="confirm-reservation-btn">Mark confirmed & notify</Button>
        </Card>
      )}

      {dn.status === "reserved" && (
        <Card className="p-6">
          <Button onClick={()=>setStatus("complete")} data-testid="mark-awaiting-ratings-btn">Dinner over → collect ratings</Button>
        </Card>
      )}

      {rsvps.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-serif text-xl">RSVP responses</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={sendRecap} data-testid="send-recap-btn">Send recap now</Button>
              <Button size="sm" variant="outline" onClick={suggestNewDate} disabled={loadingSuggest} data-testid="suggest-date-btn">
                {loadingSuggest ? "Checking…" : "Suggest new date"}
              </Button>
            </div>
          </div>
          {suggestion && (
            <div data-testid="suggestion-panel" className="mb-3 space-y-2">
              <div className="overline">Suggested dates ({suggestion.invited_count} invited)</div>
              {(suggestion.suggestions || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No dates checked.</div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-2">
                  {(suggestion.suggestions || []).map(s => (
                    <div key={s.date} data-testid="date-suggestion" className={`rounded-lg p-3 border ${s.all_available ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300" : "bg-amber-50 dark:bg-amber-900/20 border-amber-300"}`}>
                      <div className="mono font-semibold">{s.date}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {s.all_available ? "Everyone available" : `${s.available_count}/${s.total} available`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            {dn.participant_member_ids.map(mid => {
              const m = memberName(mid);
              const rsvp = rsvps.find(x=>x.member_id===mid);
              const s = rsvp?.status;
              const color = s === "yes" ? "text-emerald-600" : s === "no" ? "text-red-600" : s === "maybe" ? "text-amber-600" : "text-muted-foreground";
              return (
                <div key={mid} data-testid="rsvp-row" className="flex items-center justify-between text-sm p-3 bg-secondary/40 rounded-lg">
                  <span>{m?.first_name} {m?.last_name}</span>
                  <span className={`mono uppercase text-xs ${color}`}>{s || "no reply"}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {snapshot && snapshot.members && snapshot.members.length > 0 && (
        <Card className="p-6">
          <button onClick={()=>setShowSnapshot(s=>!s)} data-testid="toggle-snapshot-btn" className="w-full flex items-center justify-between">
            <div className="text-left">
              <div className="overline">Explainability</div>
              <div className="font-serif text-xl">Preferences at recommendation time</div>
              <div className="text-xs text-muted-foreground mt-1">
                Snapshot saved {snapshot.created_at?.slice(0,10)} · {snapshot.members.length} member(s)
                {snapshotDiff?.changes?.length > 0 && <span className="ml-2 text-primary font-medium">· {snapshotDiff.changes.length} member(s) changed since last dinner</span>}
              </div>
            </div>
            <div className="text-sm text-primary">{showSnapshot?"Hide":"Show"}</div>
          </button>
          {showSnapshot && (
            <div className="mt-4 space-y-3" data-testid="preference-snapshot-panel">
              {snapshotDiff?.changes?.length > 0 && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-4" data-testid="snapshot-diff-panel">
                  <div className="overline mb-2">What changed since last snapshot ({snapshotDiff.previous_at?.slice(0,10)})</div>
                  <div className="space-y-2">
                    {snapshotDiff.changes.map(c => (
                      <div key={c.member_id} className="text-sm">
                        <div className="font-medium">{c.first_name} {c.last_name}</div>
                        <ul className="text-xs text-muted-foreground list-disc list-inside">
                          {c.changes.map((x,i) => <li key={`${c.member_id}-chg-${i}`}>{x}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {snapshot.members.map(m => {
                const cuisines = Object.entries(m.cuisine||{}).filter(([_,v])=>v!=="neutral");
                const restById = Object.fromEntries(restaurants.map(r=>[r.id,r.name]));
                return (
                  <div key={m.member_id} className="border rounded-lg p-3 text-sm">
                    <div className="font-medium mb-1">{m.first_name} {m.last_name} <span className="text-xs mono text-muted-foreground">({formatTime(m.earliest_time)}–{formatTime(m.latest_time)}, preferred {formatTime(m.preferred_time)})</span></div>
                    {cuisines.length > 0 && <div className="text-xs text-muted-foreground">Cuisine: {cuisines.map(([k,v])=>`${k}:${v}`).join(", ")}</div>}
                    {(m.restaurant_prefs||[]).length > 0 && <div className="text-xs text-muted-foreground">Restaurants: {m.restaurant_prefs.map(rp=>`${restById[rp.restaurant_id]||"?"}:${rp.tag}`).join(", ")}</div>}
                    {Object.keys(m.characteristics||{}).length > 0 && <div className="text-xs text-muted-foreground">Vibe: {Object.entries(m.characteristics).map(([k,v])=>`${k}:${v}`).join(", ")}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <Card className="p-6">
        <h3 className="font-serif text-xl mb-3">Ratings</h3>
        <div className="space-y-2 mb-4">
          {ratings.length===0 && <div className="text-sm text-muted-foreground">No ratings yet.</div>}
          {ratings.map(r=>{ const m=memberName(r.member_id); return (
            <div key={r.id} className="flex items-start gap-3 p-3 bg-secondary/40 rounded-lg">
              <div className="flex items-center gap-1">
                {Array.from({length:5}).map((_,i)=><Star key={`${r.id}-s-${i}`} className={`w-4 h-4 ${i<r.stars?"fill-amber-400 text-amber-400":"text-muted-foreground"}`}/>)}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{m?.first_name} {m?.last_name}</div>
                {r.comment && <div className="text-xs text-muted-foreground">{r.comment}</div>}
              </div>
            </div>
          );})}
        </div>
        <div className="grid md:grid-cols-3 gap-2 items-end">
          <div>
            <Label>Member</Label>
            <select value={ratingForm.member_id} onChange={e=>setRatingForm({...ratingForm,member_id:e.target.value})} className="w-full border rounded-md h-10 px-3 bg-background" data-testid="rating-member-select">
              <option value="">Select member</option>
              {dn.participant_member_ids.map(mid=>{ const m=memberName(mid); return <option key={mid} value={mid}>{m?.first_name} {m?.last_name}</option>; })}
            </select>
          </div>
          <div>
            <Label>Stars</Label>
            <select value={ratingForm.stars} onChange={e=>setRatingForm({...ratingForm,stars:Number(e.target.value)})} className="w-full border rounded-md h-10 px-3 bg-background">
              {[1,2,3,4,5].map(n=><option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <Button onClick={submitRating} data-testid="submit-rating-btn">Submit rating</Button>
        </div>
        <div className="mt-2"><Label>Comment (optional)</Label><Textarea value={ratingForm.comment} onChange={e=>setRatingForm({...ratingForm,comment:e.target.value})}/></div>
      </Card>
    </div>
  );
}
